// Applicant run progress — Workflow Settings → Options → "Display progress bar".
//
// A single thin line above the step content, filled solid green once the run finishes.
//
// Deliberately JUST the line: no "Step 2 of 4" label and no percentage. Both were text the
// applicant does not act on, and on a small phone the label row cost about 22px at the top of
// every step — enough to push the PAN screen past the viewport and make a form that used to
// fit start scrolling. The line alone still answers the only question the applicant has
// ("how much is left?"), and the numbers it replaced are still available to screen readers
// through the ARIA attributes below.

/**
 * The step in flight counts as half a step.
 *
 * A purely completed-based bar sits at 0% on the very first screen, which reads as
 * broken, and a position-based one (step 4 of 4 → 100%) claims the run is done
 * before it is. Half-credit for the current step avoids both: the bar always shows
 * movement from the first step, rises as each step passes, and only reaches 100%
 * when the run is genuinely complete.
 */
const IN_FLIGHT_CREDIT = 0.5;

export function SessionProgressBar({
  total,
  completed,
  isComplete,
}: {
  /** Total steps in the run. */
  total: number;
  /** Steps already passed (or sent to review). */
  completed: number;
  /** Every step is finished — the bar goes full and turns green. */
  isComplete: boolean;
}) {
  if (total <= 0) return null;

  const fraction = isComplete ? 1 : Math.min((completed + IN_FLIGHT_CREDIT) / total, 1);
  const percent = Math.round(fraction * 100);
  // 1-based position of the step being worked on, clamped for the complete case.
  const currentStep = Math.min(completed + 1, total);

  return (
    // IN FLOW, at the top of the card, exactly as the designer builds it
    // (WorkflowMobilePreview.css `.sc-workflow-preview__progress`: 4px tall, full width,
    // rounded, #E8EEF9 track, 16px below it).
    //
    // It was absolutely positioned before, to cost zero layout height on a phone where the
    // PAN screen had nothing spare. That was the wrong trade and it showed: the card is
    // itself the scrolling element on a phone, so an absolute child pinned to `top: 0` sat
    // hard against the browser toolbar with no room above it, and then SCROLLED AWAY with
    // the content — a progress indicator that disappears the moment you start filling the
    // form. The height it was saving has been recovered properly instead, by removing the
    // 16px flex gap the PAN screen was paying between every one of its own rows.
    //
    // `shrink-0` matters: this sits in a flex column beside a `flex-1` body, and without it
    // a full body squeezes a 4px bar down to nothing.
    <div
      className="mb-3 h-1 w-full shrink-0 overflow-hidden rounded-full bg-[#E8EEF9]"
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      // The label and percentage are no longer drawn, so they are carried here instead —
      // a bare line with no accessible name is meaningless to a screen reader.
      aria-label="Verification progress"
      aria-valuetext={
        isComplete ? 'Verification complete' : `Step ${currentStep} of ${total}, ${percent}%`
      }
    >
      {/* No rounding of its own: the track is already a pill and clips it, and a second
          pill inside a 4px track just reads as a shorter line. */}
      <div
        className="h-full transition-[width] duration-300 ease-out"
        style={{
          width: `${percent}%`,
          backgroundColor: isComplete
            ? 'var(--brand-status-success, #00A92C)'
            : 'var(--brand-btn-fill, var(--color-primary))',
        }}
      />
    </div>
  );
}
